import { useState, useEffect } from 'react';
import priceService from 'services/lpBondsPrice';
import { NETWORK, NETWORK_DEFAULT, NETWORK_NAME } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';

/**
 * Custom hook to fetch and manage LP Bonds prices
 * Uses the PriceService singleton to prevent duplicate calls
 */
export const useLPBondsPrices = () => {
  const { storedChain } = useWalletContext();

  const [state, setState] = useState(priceService.getState());

  useEffect(() => {
    // Subscribe to price service updates
    const unsubscribe = priceService.subscribe(setState);

    // Start the service (no-op if already started)
    priceService.start();

    // Cleanup on unmount
    return unsubscribe;
  }, []);

  const getCoinPrice = () => {
    const currency = NETWORK[storedChain] ?? NETWORK_DEFAULT;
    const priceKey = currency.priceApiKey;

    return state.prices[priceKey] || 0;
  };

  const calculateUSDValue = (amount, asset = 'ethereum') => {
    const price = state.prices[asset] || 0;
    return (amount * price).toFixed(2);
  };

  const getChainPrice = (chain) => {
    if (!chain) {
      return getCoinPrice(); // Fallback to current chain's coin price
    }

    // Find chain ID by matching name in NETWORK_NAME
    const chainId = Object.keys(NETWORK_NAME).find(
      (id) => NETWORK_NAME[id]?.toLowerCase() === chain.toLowerCase(),
    );

    if (chainId && NETWORK[chainId]?.priceApiKey) {
      return state.prices[NETWORK[chainId].priceApiKey] || 0;
    }

    // Fallback to ethereum price if chain not found
    return state.prices.ethereum || 0;
  };

  return {
    prices: state.prices,
    loading: state.loading,
    error: state.error,
    // Component-friendly named properties using API keys
    ethPrice: state.prices.ethereum || 0,
    nrgPrice: state.prices.energi || 0,
    gmiPrice: state.prices.gonnamakeit || 0,
    apePrice: state.prices.apecoin || 0,
    helloPrice: state.prices['hello-labs'] || 0,
    kimaPrice: state.prices.kima || 0,
    monPrice: state.prices.monad || 0,
    bnbPrice: state.prices.binancecoin || 0,
    arbPrice: state.prices.arbitrum || 0,
    coinPrice: getCoinPrice(), // Chain-aware: ETH for Ethereum, APE for ApeChain, NRG for Energi, MON for Monad
    getChainPrice, // Data-driven: uses NETWORK constant to map chain name to priceApiKey
    calculateUSDValue,
    refetch: () => priceService.refetch(),
  };
};

export default useLPBondsPrices;
